/**
 * TOSS Web Application — is there anywhere to go back to WITHIN this application
 *
 * The one question every back control has to answer before it pops the history, and the one
 * the codebase used to answer wrong. `window.history.length > 1` counts the entries of the
 * whole TAB, other sites included: a customer who opens the portal from a link in a letter
 * arrives with a length of 2, `navigate(-1)` returns them to the mail client, and in an
 * installed PWA that is a way out of the app with no way back in.
 *
 * `history.state.idx` counts something else entirely — the entries THIS document's router
 * created. React Router writes it into every entry it makes
 * (`@remix-run/router/dist/router.js:265-271`):
 *
 *     function getHistoryState(location, index) {
 *       return { usr: location.state, key: location.key, idx: index };
 *     }
 *
 * and seeds `idx: 0` on the first entry it sees, when `history.state` carries no index of its
 * own (`router.js:336-354`) — which is exactly the case for an entry created by a navigation
 * from another document. `push` stores `getIndex() + 1`, `replace` stores `getIndex()`
 * unchanged, so a redirect never inflates the count.
 *
 * That gives the four answers the old check got wrong:
 *
 *  - **arrived from a link in a letter** — new document, no index of ours, `idx: 0` → false;
 *  - **cold start of the installed app** — same, `idx: 0` → false;
 *  - **F5 halfway down a path** — the browser restores `history.state` with the entry, so the
 *    index survives the reload → true, and the pop lands where it should;
 *  - **walked in from another screen of ours** — `idx > 0` → true.
 *
 * Nothing in `src/**` calls `history.pushState` / `replaceState` directly, so the router is
 * the only writer of `idx` and the count cannot drift. Keep it that way: a direct `pushState`
 * anywhere would desynchronise this for every back control at once.
 */

/**
 * Which entry of this document's stack we are standing on, or `null` when the router has not
 * written one — an entry created by a navigation from another document.
 *
 * Stable across `replace`, which is what makes it an identity for a PLACE rather than for a
 * router state: `location.key` changes every time a screen mirrors a filter into the address, and
 * anything keyed by it loses track of the entry it was describing.
 */
export function historyIndex(
  historyState: unknown = typeof window === 'undefined' ? null : window.history.state
): number | null {
  const idx = (historyState as { idx?: unknown } | null)?.idx;

  return typeof idx === 'number' ? idx : null;
}

/**
 * Whether the entry behind the current one was created by this application.
 *
 * The state is a parameter so the rule can be asserted without a router — pass nothing in
 * application code.
 */
export function hasInAppHistory(
  historyState: unknown = typeof window === 'undefined' ? null : window.history.state
): boolean {
  const idx = historyIndex(historyState);

  return idx !== null && idx > 0;
}

export default hasInAppHistory;
